import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { StreamSlot } from '../components/viewer/StreamSlot'
import { PtzPanel } from '../components/viewer/PtzPanel'
import { RecordingPanel } from '../components/viewer/RecordingPanel'
import { EditCameraDialog } from '../components/camera/EditCameraDialog'
import { CameraSettingsPanel } from '../components/viewer/CameraSettingsPanel'
import { Skeleton } from '../components/ui/Skeleton'
import { useToast } from '../components/ui/Toast'
import type { Camera, Site, LayoutId } from '../types'
import { LAYOUT_PRESETS, LAYOUT_MAP } from '../types'

type RightPanel = { type: 'ptz'; camera: Camera } | { type: 'recording'; camera: Camera } | null

function bestFitGrid(count: number): LayoutId {
  if (count <= 1) return '1x1'
  if (count <= 4) return '2x2'
  if (count <= 9) return '3x3'
  if (count <= 16) return '4x4'
  if (count <= 25) return '5x5'
  return '6x6'
}

export function GroupViewerPage() {
  const [searchParams] = useSearchParams()
  const groupName = searchParams.get('group') ?? ''
  const { toast } = useToast()

  const [cameras, setCameras] = useState<Camera[]>([])
  const [sites, setSites] = useState<Map<string, Site>>(new Map())
  const [loading, setLoading] = useState(true)
  const [panel, setPanel] = useState<RightPanel>(null)
  const [editCamera, setEditCamera] = useState<Camera | null>(null)
  const [settingsCamera, setSettingsCamera] = useState<Camera | null>(null)
  const [selectedSlot, setSelectedSlot] = useState(0)

  const [gridSize, setGridSize] = useState<LayoutId>('2x2')

  // Load all cameras from sites in this group
  useEffect(() => {
    async function loadGroup() {
      setLoading(true)

      // Get all sites in this group
      const { data: groupSites } = await supabase
        .from('sites')
        .select('*')
        .eq('group_name', groupName)
        .order('name')

      if (!groupSites || groupSites.length === 0) {
        setLoading(false)
        return
      }

      const siteMap = new Map<string, Site>()
      groupSites.forEach(s => siteMap.set(s.id, s as Site))
      setSites(siteMap)

      // Get all cameras from those sites
      const siteIds = groupSites.map(s => s.id)
      const { data: allCameras } = await supabase
        .from('cameras')
        .select('*')
        .in('site_id', siteIds)
        .order('cam_number')

      const cams = (allCameras ?? []) as Camera[]
      setCameras(cams)
      setGridSize(bestFitGrid(cams.length))
      setLoading(false)
    }

    if (groupName) loadGroup()
  }, [groupName])

  const handleSaveCamera = useCallback(async (
    cameraId: string,
    updates: Partial<Pick<Camera, 'name' | 'cam_ip' | 'port' | 'external_ip' | 'rtsp_path' | 'username' | 'password'>>,
  ) => {
    const { error } = await supabase
      .from('cameras')
      .update(updates)
      .eq('id', cameraId)
    if (error) throw error
    toast('Camera updated', 'success')
  }, [toast])

  const layout = LAYOUT_MAP[gridSize] ?? LAYOUT_MAP['2x2']
  const totalSlots = layout.totalSlots

  if (loading) {
    return (
      <div className="fixed inset-0 bg-content flex items-center justify-center">
        <div className="space-y-3 w-64">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 flex bg-content">
      <div className={`flex-1 flex flex-col min-w-0 ${panel ? 'mr-72' : ''}`}>
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-2 bg-sidebar/50 border-b border-card-border">
          <Link to="/" className="text-gray-500 hover:text-white text-sm">
            &larr; Dashboard
          </Link>
          <span className="text-gray-600 text-sm">|</span>
          <span className="text-white text-sm font-medium">{groupName}</span>
          <span className="text-gray-500 text-xs">{cameras.length} cameras across {sites.size} sites</span>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 px-3 py-2 bg-sidebar border-b border-card-border">
          <span className="text-xs text-gray-500 mr-2">Layout</span>
          {LAYOUT_PRESETS.map((preset) => (
            <button
              key={preset.id}
              onClick={() => setGridSize(preset.id)}
              className={`text-xs px-2.5 py-1 rounded transition-colors ${
                gridSize === preset.id
                  ? 'bg-brand text-white'
                  : 'bg-gray-100 text-gray-500 hover:text-gray-900'
              }`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div
          className="flex-1 min-h-0 p-2 gap-2"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${layout.cols}, 1fr)`,
            gridTemplateRows: `repeat(${layout.rows}, 1fr)`,
          }}
        >
          {Array.from({ length: totalSlots }, (_, i) => {
            const camera = i < cameras.length ? cameras[i] : null
            const slotStyle = layout.slotStyles?.[i]
            return (
              <StreamSlot
                key={i}
                index={i}
                camera={camera}
                isSelected={selectedSlot === i}
                onSelect={() => setSelectedSlot(i)}
                onClear={() => {}}
                onToggleHd={() => {}}
                onPtzOpen={(cam) => setPanel({ type: 'ptz', camera: cam })}
                onRecordingOpen={(cam) => setPanel({ type: 'recording', camera: cam })}
                onEditCamera={(cam) => setEditCamera(cam)}
                onSettingsOpen={(cam) => setSettingsCamera(cam)}
                onDrop={() => {}}
                style={slotStyle}
              />
            )
          })}
        </div>
      </div>

      {panel?.type === 'ptz' && (
        <PtzPanel
          camera={panel.camera}
          onClose={() => setPanel(null)}
          onOpenSettings={() => { setSettingsCamera(panel.camera); setPanel(null) }}
        />
      )}
      {panel?.type === 'recording' && (
        <RecordingPanel camera={panel.camera} onClose={() => setPanel(null)} />
      )}
      <EditCameraDialog
        open={!!editCamera}
        camera={editCamera}
        onClose={() => setEditCamera(null)}
        onSave={handleSaveCamera}
      />
      {settingsCamera && (
        <CameraSettingsPanel
          camera={settingsCamera}
          onClose={() => setSettingsCamera(null)}
        />
      )}
    </div>
  )
}
